//import THREE from "three";
import MaterialBuilder from "../rendering/material-builder";
import { HotspotSpriteMaterial, IconCanvas } from "./sprites/IconTexture";
import { isTexture } from "./deserialize-texture";
import ensureArray from '../utils/ensure-array';

/**
 * Load materials.
 * @param {Object} props Parameters.
 * @param {String} props.uuid Material UUID.
 * @param {Object} props.data Material data.
 * @param {Object} props.textures Texture data list.
 * @param {THREE.CubeTexture} props.envMap Environment map.
 * @return {Promise}
 */
async function loadMaterial({ uuid, data, textures, envMap, envMapCubeRes }) {

    let material = null;

    switch (data.type) {
        case MaterialTypes_T.Basic: material = await createBasicMaterial(); break;
        case MaterialTypes_T.Lambert: material = await createLambertMaterial(); break;
        case MaterialTypes_T.Phong: material = await createPhongMaterial(); break;
        case MaterialTypes_T.PBR: material = await createPBRMaterial(data); break;
        case MaterialTypes_T.Points: material = await createPointsMaterial(); break;
        case MaterialTypes_T.LineBasic: material = await createLineBasicMaterial(); break;
        case MaterialTypes_T.Sprite: material = await createSpriteMaterial(); break;
        case MaterialTypes_T.IconHotspot: material = await createIconHotspotMaterial(data); break;
        default:
            material = await createShaderMaterial(data);
            data.type = MaterialTypes_T.Custom;
            break;
    }

    if (data.transparent && !data.overriddenSide) { data.side = THREE.FrontSide; }

    if (data.type !== MaterialTypes_T.IconHotspot && data.type !== MaterialTypes_T.Custom) {
        applyMaterialParameters(material, data, textures, envMap, envMapCubeRes);
    }

    material.name = data.name;
    material.uuid = uuid;

    return { uuid, material };
}

/**
 * Checks if material is in use.
 * @param {Object} props Parameter.
 * @param {String} props.uuid Material UUID.
 * @param {Object} props.data Project data.
 * @return {boolean}
 */
function isMaterialInUse({ uuid, data }) {
    if (Object.keys(data.assets.objects).some(key => {
        return ensureArray(data.assets.objects[key].material).some(material => material === uuid);
    })) return true;

    console.warn(`Material ${data.assets.materials[uuid].name} (${uuid}) is unused, removing.`);

    return false;
}

export default loadMaterial;
export { loadMaterial, isMaterialInUse };

/**
 * Material types.
 */
const MaterialTypes_T = Object.freeze({
    Basic: 0,
    Lambert: 1,
    Phong: 2,
    PBR: 3,
    Points: 4,
    LineBasic: 5,
    Sprite: 6,
    IconHotspot: 7,
    Custom: 8
});

/**
 * Creates shader material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createShaderMaterial(data) {
    const params = Object.assign({}, data.script, { morphTargets: true });
    return new THREE.ShaderMaterial(params);
}

/**
 * Creates basic material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createBasicMaterial() {
    return new THREE.MeshBasicMaterial({ morphTargets: true });
}

/**
 * Creates lambert material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createLambertMaterial() {
    return new THREE.MeshLambertMaterial({ morphTargets: true });
}

/**
 * Creates phong material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createPhongMaterial() {
    return new THREE.MeshPhongMaterial({ morphTargets: true });
}

/**
 * Creates PBR material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createPBRMaterial(data) {
    return MaterialBuilder.build({ materialType: data.materialType, doNotSetValues: true });
}

/**
 * Creates points material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createPointsMaterial() {
    return new THREE.PointsMaterial({ morphTargets: true });
}

/**
 * Creates line basic material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createLineBasicMaterial() {
    return new THREE.LineBasicMaterial({ morphTargets: true });
}

/**
 * Creates sprite material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createSpriteMaterial() {
    return new THREE.SpriteMaterial();
}

/**
 * Creates icon hotspot material.
 * @param {Object} data Material data.
 * @return {Promise}
 */
async function createIconHotspotMaterial(data) {

    //Empty texture, will be replaced
    const texture = new THREE.Texture();
    texture.iconData = data.iconData;
    texture.lastUpdate = new Date();

    //Material
    const material = new HotspotSpriteMaterial(texture, new THREE.Vector2(data.iconData.scaleX, data.iconData.scaleY));
    const newMap = new IconCanvas(data.iconData);

    const image = await newMap.draw();

    const newTexture = new THREE.Texture(image);
    newTexture.needsUpdate = true;
    newTexture.iconData = data.iconData;
    newTexture.lastUpdate = new Date();

    material.setTexture(newTexture);
    material.needsUpdate = true;


    return material;
}

/**
 * Applies material parameters.
 * @param {THREE.Material} material Material to which to apply material.
 * @param {Object} data Material data.
 * @param {THREE.Texture[]} textures Texture map.
 * @param {THREE.CubeTexture} envMap Environment map.
 */
function applyMaterialParameters(material, data, textures, envMap, envMapCubeRes) {
    const supportsEnvMap = data.materialType !== "basic";
    const supportsSmoothClip = data.materialType !== "basic";

    if (data["smoothClipEnd"] !== undefined && data["smoothClipStart"] && data["smoothClipEnabled"] === undefined) {
        data.smoothClipEnabled = true;
    }

    Object.keys(data).forEach(key => {
        if (key === "layers" && data.materialType === "nested") {
            material.setLayers(data[key].map(x => {
                return {
                    distance: new THREE.Vector2().fromArray(x.distance),
                    map: x.map ? textures[x.map] : null,
                    normalMap: x.normalMap ? textures[x.normalMap] : null
                };
            }));
        } else if (key === "smoothClipEnd" || key === "smoothClipStart") {
            if (supportsSmoothClip) {
                material.uniforms[key].value = data[key];
            }
        } else if (key === "smoothClipEnabled") {
            if (supportsSmoothClip) {
                material.setSmoothClipEnabled(data[key]);
            }
        } else if (material[key] !== undefined && key !== "type") {

            if (isTexture(key)) {
                if (Q3.editMode || !Q3.browserCapabilities.allowedMaps || Q3.browserCapabilities.allowedMaps.includes(key)) {
                    const t = textures[data[key]];
                    material[key] = t;
                }
            } else if (["color", "emissive", "specular", "secondaryColor"].indexOf(key) > -1) { //if color

                material[key] = new THREE.Color(parseInt(data[key]));

            } else if (["start", "end"].indexOf(key) === -1) {//if other

                if (key === "normalScale") {

                    //it seems that earlier normalscale was {x, y} instead of just a number, this fixes it.
                    if (data[key]) {
                        if (typeof data[key] !== "number" && data[key].x) material[key].set(data[key].x, data[key].x);

                        else material[key].set(data[key], data[key]);
                    }

                } else {

                    material[key] = data[key];

                }

            }
        }
    });

    if (material.transparent) material.alphaTest = 0.01;
    if (supportsEnvMap) {
        material.envMap = envMap;

        if (data.materialType === "pbr") {
            material.envMapCubeRes = envMapCubeRes;
        }
    }

    material.needsUpdate = true;
}

export { MaterialTypes_T, applyMaterialParameters };
